// Central call log reporter (CDCSF-5447). Sends call state changes to
// /api/call-log/events. Numbers are redacted in the browser (country looked up
// first), so full numbers never leave the client. Fire-and-forget: a failure
// here must never affect the call itself.

import { authenticatedFetch } from "./session";
import { redactNumber } from "../../libs/callLog/redact";
import { locateNumber } from "../../libs/callLog/locate";

const EVENTS_URL = "/api/call-log/events";
export const HEARTBEAT_INTERVAL_MS = 60 * 1000;

const heartbeats = new Map();
let enabledPromise = null;

function isEnabled() {
  if (!enabledPromise) {
    enabledPromise = fetch("/api/me", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((me) => Boolean(me?.callLogEnabled))
      .catch(() => {
        enabledPromise = null; // retry on the next call
        return false;
      });
  }
  return enabledPromise;
}

function toIso(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function party(rawNumber) {
  return { number: redactNumber(rawNumber), country: locateNumber(rawNumber).country };
}

/**
 * Turn a CTI call-map entry into a redacted payload, or null when the call is
 * out of scope (internal Teams-to-Teams: no phone number for the other party).
 */
export function describeCall(callId, meta) {
  const direction =
    meta?.direction === "Outbound" ? "outbound" : meta?.direction === "Inbound" ? "inbound" : null;
  if (!callId || !direction) return null;

  const externalRaw = direction === "inbound" ? meta.callFromPhone : meta.callToPhone;
  if (!externalRaw || !redactNumber(externalRaw)) return null;

  const caller = party(meta.callFromPhone);
  const called = party(meta.callToPhone);
  return {
    callId: String(callId),
    direction,
    startedAt: toIso(meta.startedAt),
    connectedAt: toIso(meta.answeredAt),
    callerNumber: caller.number,
    calledNumber: called.number,
    callerCountry: caller.country,
    calledCountry: called.country,
  };
}

async function send(payload) {
  try {
    if (!(await isEnabled())) return;
    await authenticatedFetch(EVENTS_URL, {
      method: "POST",
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    console.warn(`[callLog] ${payload.event} for ${payload.callId} not sent:`, err?.message);
  }
}

function stopHeartbeat(callId) {
  const timer = heartbeats.get(callId);
  if (timer) clearInterval(timer);
  heartbeats.delete(callId);
}

/** Call once the call reaches Connected. Starts a heartbeat that keeps it "live". */
export function reportCallConnected(callId, meta) {
  try {
    const call = describeCall(callId, meta);
    if (!call) return;
    if (!call.connectedAt) call.connectedAt = new Date().toISOString();

    send({ ...call, event: "connected" });
    if (!heartbeats.has(call.callId)) {
      // Stop on our own if the ACS call has ended but no end was reported (e.g. a
      // late Disconnected ignored as stale); the server then ages it out of "live".
      const acsCall = meta?.acsCall;
      const timer = setInterval(() => {
        if (acsCall?.state === "Disconnected") {
          stopHeartbeat(call.callId);
          return;
        }
        send({ event: "heartbeat", callId: call.callId });
      }, HEARTBEAT_INTERVAL_MS);
      heartbeats.set(call.callId, timer);
    }
  } catch (err) {
    console.warn("[callLog] connected not reported:", err?.message);
  }
}

/**
 * Call when the call disconnects. Only calls that connected are reported:
 * unanswered ones are left to Sync, which skips calls only when an app leg
 * connected, so each call is counted once.
 */
export function reportCallEnded(callId, meta, endedAt = Date.now()) {
  try {
    stopHeartbeat(String(callId));
    const call = describeCall(callId, meta);
    if (!call || !call.connectedAt) return;
    send({ ...call, event: "ended", endedAt: toIso(endedAt) });
  } catch (err) {
    console.warn("[callLog] ended not reported:", err?.message);
  }
}

// Test hook
export function resetCallLogReporterForTests() {
  heartbeats.forEach((timer) => clearInterval(timer));
  heartbeats.clear();
  enabledPromise = null;
}
